/**
 * React hooks for feature flag functionality
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import {
  getFeatureFlag,
  setFeatureFlagOverride,
  clearFeatureFlagOverride,
  clearAllFeatureFlagOverrides,
  getFeatureFlagOverrides,
  hasFeatureFlagOverride,
  getAllFeatureFlags,
  getFeatureFlagConfig,
  registerFeatureFlag,
  unregisterFeatureFlag,
  isFeatureEnabledForEnvironment,
  type FeatureFlagConfig,
  type FeatureFlagName,
} from "../utils/featureFlags";
import { getEnvironment, type Environment } from "../utils/environment";

/** Shallow equality for flag records (string → boolean). */
function flagsEqual(a: Record<string, boolean>, b: Record<string, boolean>): boolean {
  const aKeys = Object.keys(a);
  if (aKeys.length !== Object.keys(b).length) return false;
  return aKeys.every((key) => a[key] === b[key]);
}

/** Shallow equality for override maps (string → boolean). */
function overridesEqual(a: Map<string, boolean>, b: Map<string, boolean>): boolean {
  if (a.size !== b.size) return false;
  for (const [key, value] of a) {
    if (!b.has(key) || b.get(key) !== value) return false;
  }
  return true;
}

/**
 * Hook return type for useFeatureFlag
 */
export interface UseFeatureFlagReturn {
  /** Current value of the feature flag */
  value: boolean;
  /** Whether the flag has a runtime override */
  hasOverride: boolean;
  /** Set a runtime override for this flag */
  setOverride: (value: boolean) => void;
  /** Clear the runtime override for this flag */
  clearOverride: () => void;
  /** Feature flag configuration */
  config: FeatureFlagConfig | undefined;
}

/**
 * React hook for accessing a single feature flag
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { value: showNewDashboard } = useFeatureFlag('experimental.new-dashboard');
 *
 *   return (
 *     <div>
 *       {showNewDashboard && <NewDashboard />}
 *       {!showNewDashboard && <OldDashboard />}
 *     </div>
 *   );
 * }
 * ```
 *
 * @param flagName - The name of the feature flag
 * @param config - Optional custom configuration for the flag
 * @returns Feature flag value and utilities
 */
export function useFeatureFlag(flagName: string, config?: FeatureFlagConfig): UseFeatureFlagReturn {
  const [value, setValue] = useState(() => getFeatureFlag(flagName, config));
  const [hasOverride, setHasOverride] = useState(() => hasFeatureFlagOverride(flagName));

  // Update flag value when dependencies change
  useEffect(() => {
    const updateFlag = () => {
      // Both states are booleans, so React's Object.is bail-out already keeps
      // this 1s poll from re-rendering consumers when nothing changed.
      setValue(getFeatureFlag(flagName, config));
      setHasOverride(hasFeatureFlagOverride(flagName));
    };

    // Update immediately
    updateFlag();

    // Listen for environment changes that might affect flag values
    const interval = setInterval(updateFlag, 1000);

    return () => clearInterval(interval);
  }, [flagName, config]);

  const setOverride = useCallback(
    (newValue: boolean) => {
      setFeatureFlagOverride(flagName, newValue);
      setValue(newValue);
      setHasOverride(true);
    },
    [flagName],
  );

  const clearOverride = useCallback(() => {
    clearFeatureFlagOverride(flagName);
    setValue(getFeatureFlag(flagName, config));
    setHasOverride(false);
  }, [flagName, config]);

  const flagConfig = useMemo(() => getFeatureFlagConfig(flagName), [flagName]);

  return {
    value,
    hasOverride,
    setOverride,
    clearOverride,
    config: flagConfig,
  };
}

/**
 * Hook return type for useFeatureFlags
 */
export interface UseFeatureFlagsReturn {
  /** All feature flags with their current values */
  flags: Record<string, boolean>;
  /** All runtime overrides */
  overrides: Map<string, boolean>;
  /** Set a runtime override for a flag */
  setOverride: (flagName: string, value: boolean) => void;
  /** Clear a runtime override for a flag */
  clearOverride: (flagName: string) => void;
  /** Clear all runtime overrides */
  clearAllOverrides: () => void;
  /** Check if a flag has an override */
  hasOverride: (flagName: string) => boolean;
}

/**
 * React hook for accessing multiple feature flags
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { flags, setOverride } = useFeatureFlags();
 *
 *   return (
 *     <div>
 *       {flags['experimental.new-dashboard'] && <NewDashboard />}
 *       {flags['dev.devtools'] && <DevTools />}
 *       <button onClick={() => setOverride('experimental.new-dashboard', true)}>
 *         Enable New Dashboard
 *       </button>
 *     </div>
 *   );
 * }
 * ```
 *
 * @returns All feature flags and override utilities
 */
export function useFeatureFlags(): UseFeatureFlagsReturn {
  const [flags, setFlags] = useState(() => getAllFeatureFlags());
  const [overrides, setOverrides] = useState(() => getFeatureFlagOverrides());

  // Re-read the source of truth, but keep the previous state objects when the
  // contents are unchanged — getAllFeatureFlags/getFeatureFlagOverrides return
  // fresh object/Map identities on every call, and returning `prev` stops the
  // 1s poll below from re-rendering every consumer each tick (same guard as
  // use-environment.tsx).
  const syncFlags = useCallback(() => {
    setFlags((prev) => {
      const next = getAllFeatureFlags();
      return flagsEqual(prev, next) ? prev : next;
    });
    setOverrides((prev) => {
      const next = getFeatureFlagOverrides();
      return overridesEqual(prev, next) ? prev : next;
    });
  }, []);

  // Update flags when dependencies change
  useEffect(() => {
    // Update immediately
    syncFlags();

    // Listen for environment changes that might affect flag values
    const interval = setInterval(syncFlags, 1000);

    return () => clearInterval(interval);
  }, [syncFlags]);

  const setOverride = useCallback(
    (flagName: string, value: boolean) => {
      setFeatureFlagOverride(flagName, value);
      syncFlags();
    },
    [syncFlags],
  );

  const clearOverride = useCallback(
    (flagName: string) => {
      clearFeatureFlagOverride(flagName);
      syncFlags();
    },
    [syncFlags],
  );

  const clearAllOverrides = useCallback(() => {
    clearAllFeatureFlagOverrides();
    syncFlags();
  }, [syncFlags]);

  const hasOverride = useCallback((flagName: string) => {
    return hasFeatureFlagOverride(flagName);
  }, []);

  return {
    flags,
    overrides,
    setOverride,
    clearOverride,
    clearAllOverrides,
    hasOverride,
  };
}

/**
 * Hook for conditional rendering based on feature flags
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { showIf } = useFeatureFlagConditional();
 *
 *   return (
 *     <div>
 *       {showIf('experimental.new-dashboard', <NewDashboard />)}
 *       {showIf('dev.devtools', <DevTools />)}
 *     </div>
 *   );
 * }
 * ```
 */
export function useFeatureFlagConditional() {
  const { flags } = useFeatureFlags();

  return {
    /**
     * Show component only if feature flag is enabled
     */
    showIf: <T,>(flagName: string, component: T): T | null => {
      return flags[flagName] ? component : null;
    },

    /**
     * Show component only if feature flag is disabled
     */
    showIfNot: <T,>(flagName: string, component: T): T | null => {
      return !flags[flagName] ? component : null;
    },
  };
}

/**
 * Hook for environment-specific feature flag checking
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { isEnabledForEnvironment } = useEnvironmentFeatureFlags();
 *
 *   return (
 *     <div>
 *       {isEnabledForEnvironment('navigation.electron-specific', 'electron') && (
 *         <ElectronNavigation />
 *       )}
 *     </div>
 *   );
 * }
 * ```
 */
export function useEnvironmentFeatureFlags() {
  const { environment } = getEnvironment();

  return {
    /**
     * Check if a feature flag is enabled for a specific environment
     */
    isEnabledForEnvironment: (flagName: string, targetEnvironment: Environment): boolean => {
      return isFeatureEnabledForEnvironment(flagName, targetEnvironment);
    },

    /**
     * Check if a feature flag is enabled for the current environment
     */
    isEnabledForCurrentEnvironment: (flagName: string): boolean => {
      return isFeatureEnabledForEnvironment(flagName, environment);
    },
  };
}

/**
 * Hook for type-safe feature flag access
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { isEnabled } = useTypedFeatureFlags();
 *
 *   return (
 *     <div>
 *       {isEnabled('experimental.new-dashboard') && <NewDashboard />}
 *     </div>
 *   );
 * }
 * ```
 */
export function useTypedFeatureFlags() {
  const { flags } = useFeatureFlags();

  return {
    /**
     * Type-safe feature flag checker
     */
    isEnabled: (flagName: FeatureFlagName): boolean => {
      return flags[flagName] || false;
    },
  };
}

/**
 * Hook for feature flag management (registering/unregistering flags)
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { registerFlag, unregisterFlag } = useFeatureFlagManagement();
 *
 *   useEffect(() => {
 *     registerFlag({
 *       name: 'custom.feature',
 *       defaultValue: false,
 *       description: 'Custom feature flag'
 *     });
 *
 *     return () => {
 *       unregisterFlag('custom.feature');
 *     };
 *   }, []);
 * }
 * ```
 */
export function useFeatureFlagManagement() {
  return {
    /**
     * Register a new feature flag configuration
     */
    registerFlag: (config: FeatureFlagConfig) => {
      registerFeatureFlag(config);
    },

    /**
     * Unregister a feature flag configuration
     */
    unregisterFlag: (flagName: string) => {
      unregisterFeatureFlag(flagName);
    },
  };
}
